<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Loyalty Programme</h5>
        <p class="page-sub">Manage loyalty point rules and customer balances</p>
      </div>
      <button v-if="permission?.manage_loyalty && activeTab === 'rules'" class="btn-primary" @click="openCreate">
        <font-awesome-icon icon="plus" class="me-1" /> New Rule
      </button>
    </div>

    <div class="tabs">
      <button :class="['tab', { 'tab--active': activeTab === 'rules' }]" @click="activeTab = 'rules'">Point Rules</button>
      <button :class="['tab', { 'tab--active': activeTab === 'ledger' }]" @click="activeTab = 'ledger'">Customer Balances</button>
    </div>

    <!-- ── Rules tab ───────────────────────────────────────────────────── -->
    <div v-if="activeTab === 'rules'" class="card">
      <div v-if="loading" class="loading-state"><div class="spinner"></div><p>Loading rules...</p></div>
      <div v-else-if="!rules.length" class="empty-state">
        <font-awesome-icon icon="star" class="empty-icon" />
        <p>No loyalty rules configured</p>
        <span>Create rules to define how customers earn points</span>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr><th>Rule Name</th><th>Trigger</th><th>Points / Unit</th><th>Min. Amount</th><th>Status</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="r in rules" :key="r.id">
            <td class="name-col">{{ r.name }}</td>
            <td><span class="trigger-badge">{{ r.trigger_type }}</span></td>
            <td>{{ r.points_per_unit }}</td>
            <td>{{ r.minimum_amount ? r.minimum_amount : '—' }}</td>
            <td><span :class="['status-badge', r.is_active ? 'status--active' : 'status--inactive']">{{ r.is_active ? 'Active' : 'Inactive' }}</span></td>
            <td>
              <div class="row-actions">
                <button v-if="permission?.manage_loyalty" class="btn-link btn-link--edit" @click="openEdit(r)">Edit</button>
                <button v-if="permission?.manage_loyalty" class="btn-link btn-link--del" @click="confirmDelete(r)">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── Customer Balances tab ───────────────────────────────────────── -->
    <div v-if="activeTab === 'ledger'" class="card">
      <div class="filter-bar">
        <input v-model="ledgerSearch" @input="debouncedLedger" class="filter-input" placeholder="Search customer name..." />
      </div>
      <div v-if="ledgerLoading" class="loading-state"><div class="spinner"></div><p>Loading balances...</p></div>
      <div v-else-if="!ledger.length" class="empty-state">
        <font-awesome-icon icon="coins" class="empty-icon" />
        <p>No customers found</p>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr><th>Customer</th><th>Balance</th><th>Lifetime Earned</th><th>Lifetime Redeemed</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="c in ledger" :key="c.id">
            <td class="name-col">{{ c.customer_name }}</td>
            <td><span class="balance-chip">{{ c.balance ?? 0 }} pts</span></td>
            <td class="text-green">+{{ c.lifetime_earned ?? 0 }}</td>
            <td class="text-red">-{{ c.lifetime_redeemed ?? 0 }}</td>
            <td>
              <div class="row-actions">
                <button class="btn-link btn-link--history" @click="openHistory(c)">History</button>
                <button v-if="permission?.manage_loyalty" class="btn-link btn-link--earn" @click="openEarn(c)">Award</button>
                <button v-if="permission?.manage_loyalty" class="btn-link btn-link--redeem" @click="openRedeem(c)">Redeem</button>
                <button v-if="permission?.manage_loyalty" class="btn-link btn-link--adjust" @click="openAdjust(c)">Adjust</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="ledgerMeta && ledgerMeta.last_page > 1" class="pagination">
        <button :disabled="ledgerMeta.current_page <= 1" @click="changeLedgerPage(ledgerMeta.current_page - 1)" class="pg-btn">‹ Prev</button>
        <span class="pg-info">Page {{ ledgerMeta.current_page }} of {{ ledgerMeta.last_page }}</span>
        <button :disabled="ledgerMeta.current_page >= ledgerMeta.last_page" @click="changeLedgerPage(ledgerMeta.current_page + 1)" class="pg-btn">Next ›</button>
      </div>
    </div>

    <!-- ── History Drawer ──────────────────────────────────────────────── -->
    <div class="drawer-backdrop" :class="{ open: historyOpen }" @click.self="historyOpen = false"></div>
    <div class="drawer" :class="{ open: historyOpen }">
      <div class="drawer-header">
        <div class="drawer-header-left">
          <div class="drawer-icon"><font-awesome-icon icon="history" /></div>
          <div>
            <p class="drawer-title">Point History</p>
            <p class="drawer-sub">{{ historyCustomer?.customer_name }}</p>
          </div>
        </div>
        <button class="modal-close" @click="historyOpen = false"><font-awesome-icon icon="times" /></button>
      </div>
      <div class="drawer-body">
        <!-- Balance summary -->
        <div class="balance-summary">
          <div class="balance-summary-card balance-summary-card--blue">
            <p class="bs-val">{{ historyCustomer?.balance ?? 0 }}</p>
            <p class="bs-lbl">Current Balance</p>
          </div>
          <div class="balance-summary-card balance-summary-card--green">
            <p class="bs-val">+{{ historyCustomer?.lifetime_earned ?? 0 }}</p>
            <p class="bs-lbl">Lifetime Earned</p>
          </div>
          <div class="balance-summary-card balance-summary-card--red">
            <p class="bs-val">-{{ historyCustomer?.lifetime_redeemed ?? 0 }}</p>
            <p class="bs-lbl">Lifetime Redeemed</p>
          </div>
        </div>

        <div v-if="historyLoading" class="loading-state" style="padding:32px 0"><div class="spinner"></div></div>
        <div v-else-if="!history.length" class="empty-state" style="padding:32px 0">
          <font-awesome-icon icon="history" style="font-size:28px;opacity:.3" />
          <p style="margin:0;font-size:13px">No transactions yet</p>
        </div>
        <table v-else class="resp-table">
          <thead>
            <tr><th>Type</th><th>Points</th><th>Balance</th><th>Rule / Note</th><th>Date</th></tr>
          </thead>
          <tbody>
            <tr v-for="h in history" :key="h.id">
              <td><span :class="['type-chip', `type--${h.type}`]">{{ h.type }}</span></td>
              <td :class="h.points >= 0 ? 'text-green' : 'text-red'">{{ h.points >= 0 ? '+' : '' }}{{ h.points }}</td>
              <td class="fw-600">{{ h.balance_after }}</td>
              <td class="note-col">{{ h.rule?.name || h.notes || '—' }}</td>
              <td class="resp-date">{{ fmtDate(h.created_at) }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="historyMeta && historyMeta.last_page > 1" class="pagination" style="border-top:none;padding-top:8px">
          <button :disabled="historyMeta.current_page <= 1" @click="changeHistoryPage(historyMeta.current_page - 1)" class="pg-btn">‹</button>
          <span class="pg-info">{{ historyMeta.current_page }} / {{ historyMeta.last_page }}</span>
          <button :disabled="historyMeta.current_page >= historyMeta.last_page" @click="changeHistoryPage(historyMeta.current_page + 1)" class="pg-btn">›</button>
        </div>
      </div>
    </div>

    <!-- ── Award Points Modal ──────────────────────────────────────────── -->
    <div class="modal-overlay" :class="{ open: !!earnTarget }" @click.self="earnTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <div class="modal-header-icon modal-header-icon--green"><font-awesome-icon icon="plus-circle" /></div>
          <div>
            <span class="modal-title">Award Points</span>
            <p class="modal-sub">{{ earnTarget?.customer_name }}</p>
          </div>
          <button class="modal-close" @click="earnTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">Rule <span class="opt">(optional)</span></label>
            <select v-model="earnForm.rule_id" class="form-input">
              <option value="">— No rule —</option>
              <option v-for="r in activeRules" :key="r.id" :value="r.id">{{ r.name }} ({{ r.points_per_unit }} pts / {{ r.trigger_type }})</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Points to Award <span class="req">*</span></label>
            <input v-model.number="earnForm.points" type="number" min="1" class="form-input" required />
          </div>
          <div class="form-group">
            <label class="form-label">Notes <span class="opt">(optional)</span></label>
            <input v-model="earnForm.notes" class="form-input" placeholder="e.g. Purchase #INV-001" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="earnTarget = null">Cancel</button>
          <button class="btn-earn" :disabled="!earnForm.points || submitting" @click="doEarn">
            <font-awesome-icon icon="plus-circle" class="me-1" />
            {{ submitting ? 'Awarding…' : 'Award Points' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Redeem Points Modal ─────────────────────────────────────────── -->
    <div class="modal-overlay" :class="{ open: !!redeemTarget }" @click.self="redeemTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <div class="modal-header-icon modal-header-icon--amber"><font-awesome-icon icon="minus-circle" /></div>
          <div>
            <span class="modal-title">Redeem Points</span>
            <p class="modal-sub">{{ redeemTarget?.customer_name }} — Balance: <strong>{{ redeemTarget?.balance ?? 0 }} pts</strong></p>
          </div>
          <button class="modal-close" @click="redeemTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">Points to Redeem <span class="req">*</span></label>
            <input v-model.number="redeemForm.points" type="number" min="1" :max="redeemTarget?.balance" class="form-input" required />
            <p v-if="redeemForm.points > (redeemTarget?.balance ?? 0)" class="field-error">Exceeds available balance of {{ redeemTarget?.balance }} pts</p>
          </div>
          <div class="form-group">
            <label class="form-label">Notes <span class="opt">(optional)</span></label>
            <input v-model="redeemForm.notes" class="form-input" placeholder="e.g. Discount applied to order" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="redeemTarget = null">Cancel</button>
          <button class="btn-redeem" :disabled="!redeemForm.points || redeemForm.points > (redeemTarget?.balance ?? 0) || submitting" @click="doRedeem">
            <font-awesome-icon icon="minus-circle" class="me-1" />
            {{ submitting ? 'Redeeming…' : 'Redeem Points' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Adjust Points Modal ─────────────────────────────────────────── -->
    <div class="modal-overlay" :class="{ open: !!adjustTarget }" @click.self="adjustTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <div class="modal-header-icon modal-header-icon--grey"><font-awesome-icon icon="sliders-h" /></div>
          <div>
            <span class="modal-title">Adjust Points</span>
            <p class="modal-sub">{{ adjustTarget?.customer_name }} — Balance: <strong>{{ adjustTarget?.balance ?? 0 }} pts</strong></p>
          </div>
          <button class="modal-close" @click="adjustTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="adj-hint">Use a positive number to add points or a negative number to deduct.</div>
          <div class="form-group">
            <label class="form-label">Adjustment <span class="req">*</span></label>
            <input v-model.number="adjustForm.points" type="number" class="form-input" placeholder="e.g. 50 or -20" required />
          </div>
          <div class="form-group">
            <label class="form-label">Reason <span class="req">*</span></label>
            <input v-model="adjustForm.notes" class="form-input" placeholder="Reason for adjustment" required />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="adjustTarget = null">Cancel</button>
          <button class="btn-adjust" :disabled="!adjustForm.points || !adjustForm.notes || submitting" @click="doAdjust">
            <font-awesome-icon icon="sliders-h" class="me-1" />
            {{ submitting ? 'Saving…' : 'Apply Adjustment' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Create / Edit Rule Modal ───────────────────────────────────── -->
    <div class="modal-overlay" :class="{ open: modalOpen }" @click.self="closeModal">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">{{ editing ? 'Edit Rule' : 'New Rule' }}</span>
          <button class="modal-close" @click="closeModal"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="submit">
          <div class="modal-body">
            <div class="form-group form-group--full">
              <label class="form-label">Rule Name <span class="req">*</span></label>
              <input v-model="form.name" class="form-input" required />
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Trigger Event</label>
                <select v-model="form.trigger_type" class="form-input">
                  <option value="purchase">Purchase</option>
                  <option value="referral">Referral</option>
                  <option value="review">Review</option>
                  <option value="birthday">Birthday</option>
                  <option value="signup">Sign-up</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">Points per Unit <span class="req">*</span></label>
                <input v-model.number="form.points_per_unit" type="number" min="1" class="form-input" required />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Minimum Amount <span class="opt">(optional)</span></label>
                <input v-model.number="form.minimum_amount" type="number" min="0" class="form-input" placeholder="0.00" />
              </div>
              <div class="form-group" style="justify-content:flex-end;padding-bottom:4px">
                <label class="toggle-label" style="margin-top:auto">
                  <input type="checkbox" v-model="form.is_active" /> Active
                </label>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-ghost" @click="closeModal">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="submitting">{{ submitting ? 'Saving…' : (editing ? 'Save Changes' : 'Create Rule') }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- ── Delete Rule Confirm ────────────────────────────────────────── -->
    <div class="modal-overlay" :class="{ open: !!deleteTarget }" @click.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header"><span class="modal-title">Delete Rule</span><button class="modal-close" @click="deleteTarget = null"><font-awesome-icon icon="times" /></button></div>
        <div class="modal-body"><p>Delete rule <strong>{{ deleteTarget?.name }}</strong>? This cannot be undone.</p></div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" @click="doDelete" :disabled="submitting">{{ submitting ? 'Deleting…' : 'Delete' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
export default {
  setup() { return { toast: useToast(), store: useStore() }; },
  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
    activeRules() { return this.rules.filter(r => r.is_active); },
  },
  data() {
    return {
      activeTab: 'rules',
      // rules
      rules: [], loading: false,
      modalOpen: false, editing: null, deleteTarget: null,
      form: { name: '', trigger_type: 'purchase', points_per_unit: 10, minimum_amount: '', is_active: true },
      // balances
      ledger: [], ledgerLoading: false, ledgerSearch: '', ledgerMeta: null, ledgerPage: 1,
      // history drawer
      historyOpen: false, historyCustomer: null,
      history: [], historyLoading: false, historyMeta: null, historyPage: 1,
      // earn / redeem / adjust
      earnTarget: null,   earnForm:   { rule_id: '', points: null, notes: '' },
      redeemTarget: null, redeemForm: { points: null, notes: '' },
      adjustTarget: null, adjustForm: { points: null, notes: '' },
      submitting: false,
      debounceTimer: null,
    };
  },
  mounted() { this.fetchRules(); },
  watch: {
    activeTab(tab) { if (tab === 'ledger') this.fetchLedger(); },
  },
  methods: {
    // ── Rules ─────────────────────────────────────────────────────────
    async fetchRules() {
      this.loading = true;
      try {
        const { data } = await axios.get('/crm/loyalty/rules');
        this.rules = data.data?.data ?? data.data ?? data ?? [];
      } catch { this.toast.error('Failed to load rules'); }
      finally { this.loading = false; }
    },
    openCreate() {
      this.editing = null;
      this.form = { name: '', trigger_type: 'purchase', points_per_unit: 10, minimum_amount: '', is_active: true };
      this.modalOpen = true;
    },
    openEdit(r) {
      this.editing = r.id;
      this.form = { name: r.name, trigger_type: r.trigger_type, points_per_unit: r.points_per_unit, minimum_amount: r.minimum_amount ?? '', is_active: !!r.is_active };
      this.modalOpen = true;
    },
    closeModal() { this.modalOpen = false; },
    confirmDelete(r) { this.deleteTarget = r; },
    async submit() {
      this.submitting = true;
      try {
        const payload = { ...this.form, minimum_amount: this.form.minimum_amount || null };
        if (this.editing) { await axios.put(`/crm/loyalty/rules/${this.editing}`, payload); this.toast.success('Rule updated'); }
        else              { await axios.post('/crm/loyalty/rules', payload); this.toast.success('Rule created'); }
        this.closeModal(); this.fetchRules();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save'); }
      finally { this.submitting = false; }
    },
    async doDelete() {
      this.submitting = true;
      try { await axios.delete(`/crm/loyalty/rules/${this.deleteTarget.id}`); this.toast.success('Rule deleted'); this.deleteTarget = null; this.fetchRules(); }
      catch { this.toast.error('Failed to delete'); }
      finally { this.submitting = false; }
    },

    // ── Balances ──────────────────────────────────────────────────────
    async fetchLedger() {
      this.ledgerLoading = true;
      try {
        const { data } = await axios.get('/crm/loyalty/customers/balance', { params: { search: this.ledgerSearch, page: this.ledgerPage } });
        this.ledger     = data.data?.data ?? data.data ?? data ?? [];
        this.ledgerMeta = data.data?.meta ?? data.meta ?? null;
      } catch { this.toast.error('Failed to load balances'); }
      finally { this.ledgerLoading = false; }
    },
    debouncedLedger() { clearTimeout(this.debounceTimer); this.debounceTimer = setTimeout(() => { this.ledgerPage = 1; this.fetchLedger(); }, 400); },
    changeLedgerPage(p) { this.ledgerPage = p; this.fetchLedger(); },

    // ── History drawer ────────────────────────────────────────────────
    async openHistory(c) {
      this.historyCustomer = c;
      this.historyOpen     = true;
      this.historyPage     = 1;
      this.history         = [];
      this.historyMeta     = null;
      await this.loadHistory(c.id, 1);
    },
    async loadHistory(id, page) {
      this.historyLoading = true;
      try {
        const { data } = await axios.get(`/crm/loyalty/customers/${id}/ledger`, { params: { page, per_page: 15 } });
        this.history     = data.data?.data ?? data.data ?? [];
        this.historyMeta = data.data?.meta ?? data.meta ?? null;
      } catch { /* silent */ }
      finally { this.historyLoading = false; }
    },
    async changeHistoryPage(p) {
      this.historyPage = p;
      await this.loadHistory(this.historyCustomer.id, p);
    },

    // ── Award ─────────────────────────────────────────────────────────
    openEarn(c) {
      this.earnTarget = c;
      this.earnForm   = { rule_id: '', points: null, notes: '' };
    },
    async doEarn() {
      this.submitting = true;
      try {
        const payload = { points: this.earnForm.points, notes: this.earnForm.notes };
        if (this.earnForm.rule_id) payload.rule_id = this.earnForm.rule_id;
        const { data } = await axios.post(`/crm/loyalty/customers/${this.earnTarget.id}/earn`, payload);
        if (data.status) {
          this.toast.success(`${this.earnForm.points} points awarded`);
          this.earnTarget = null;
          this.fetchLedger();
          if (this.historyOpen && this.historyCustomer?.id === this.earnTarget?.id) {
            this.loadHistory(this.historyCustomer.id, 1);
          }
        } else {
          this.toast.error(data.message);
        }
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to award points'); }
      finally { this.submitting = false; }
    },

    // ── Redeem ────────────────────────────────────────────────────────
    openRedeem(c) {
      this.redeemTarget = c;
      this.redeemForm   = { points: null, notes: '' };
    },
    async doRedeem() {
      this.submitting = true;
      try {
        const { data } = await axios.post(`/crm/loyalty/customers/${this.redeemTarget.id}/redeem`, this.redeemForm);
        if (data.status) {
          this.toast.success(`${this.redeemForm.points} points redeemed`);
          this.redeemTarget = null;
          this.fetchLedger();
        } else {
          this.toast.error(data.message);
        }
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to redeem points'); }
      finally { this.submitting = false; }
    },

    // ── Adjust ────────────────────────────────────────────────────────
    openAdjust(c) {
      this.adjustTarget = c;
      this.adjustForm   = { points: null, notes: '' };
    },
    async doAdjust() {
      this.submitting = true;
      try {
        const { data } = await axios.post(`/crm/loyalty/customers/${this.adjustTarget.id}/adjust`, this.adjustForm);
        if (data.status) {
          this.toast.success('Adjustment applied');
          this.adjustTarget = null;
          this.fetchLedger();
        } else {
          this.toast.error(data.message);
        }
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to apply adjustment'); }
      finally { this.submitting = false; }
    },

    fmtDate(v) { return v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },
  },
};
</script>

<style scoped>
.page-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:20px}
.page-title{font-size:20px;font-weight:700;color:#1a2b45;margin:0 0 3px}
.page-sub{font-size:13px;color:#7a90b0;margin:0}
.tabs{display:flex;gap:4px;margin-bottom:16px;background:#f4f7fb;border-radius:10px;padding:4px;width:fit-content}
.tab{padding:7px 18px;border-radius:8px;border:none;background:transparent;font-size:13px;font-weight:600;color:#7a90b0;cursor:pointer;transition:all .15s}
.tab--active{background:#fff;color:#004AAD;box-shadow:0 1px 4px rgba(0,0,0,.08)}
.filter-bar{display:flex;gap:10px;padding:14px 14px 0}
.filter-input{padding:9px 13px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;flex:1;outline:none}
.filter-input:focus{border-color:#004AAD}
.btn-primary{background:#004AAD;color:#fff;border:none;padding:9px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-primary:hover{background:#003b8e}
.btn-primary:disabled{opacity:.55;cursor:not-allowed}
.btn-ghost{background:#f4f7fb;color:#3d5068;border:1px solid #e4eaf2;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger{background:#e03535;color:#fff;border:none;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger:disabled{opacity:.55;cursor:not-allowed}
.btn-earn{background:#166534;color:#fff;border:none;padding:8px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-earn:hover{background:#14532d}
.btn-earn:disabled{opacity:.55;cursor:not-allowed}
.btn-redeem{background:#92400e;color:#fff;border:none;padding:8px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-redeem:hover{background:#78350f}
.btn-redeem:disabled{opacity:.55;cursor:not-allowed}
.btn-adjust{background:#1d4ed8;color:#fff;border:none;padding:8px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-adjust:hover{background:#1e40af}
.btn-adjust:disabled{opacity:.55;cursor:not-allowed}
.card{background:#fff;border-radius:12px;border:1.5px solid #e8edf5;overflow:hidden}
.loading-state,.empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:56px 24px;color:#7a90b0;gap:8px}
.empty-icon{font-size:36px;opacity:.4}
.spinner{width:28px;height:28px;border:3px solid #e8edf5;border-top-color:#004AAD;border-radius:50%;animation:spin .7s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.data-table{width:100%;border-collapse:collapse;font-size:13px}
.data-table th{background:#f4f7fb;font-weight:600;color:#5a6a85;padding:11px 14px;text-align:left;white-space:nowrap}
.data-table td{padding:11px 14px;border-top:1px solid #f0f4fa;color:#1a2b45;vertical-align:middle}
.data-table tbody tr:hover{background:#f9fbff}
.name-col{font-weight:600}
.row-actions{display:flex;gap:8px;flex-wrap:wrap}
.btn-link{background:none;border:none;font-size:12.5px;cursor:pointer;padding:0;font-weight:600}
.btn-link--edit{color:#004AAD}
.btn-link--del{color:#e03535}
.btn-link--history{color:#0891b2}
.btn-link--earn{color:#166534}
.btn-link--redeem{color:#92400e}
.btn-link--adjust{color:#1d4ed8}
.status-badge{display:inline-block;font-size:11px;font-weight:700;border-radius:20px;padding:2px 9px}
.status--active{background:#dcfce7;color:#166534}
.status--inactive{background:#f3f4f6;color:#6b7280}
.trigger-badge{display:inline-block;font-size:11px;font-weight:700;border-radius:20px;padding:2px 9px;background:#e0f2fe;color:#0369a1;text-transform:capitalize}
.balance-chip{display:inline-block;font-size:12px;font-weight:800;background:#f0fdf4;color:#166534;border-radius:20px;padding:2px 10px}
.text-green{color:#166534;font-weight:600}
.text-red{color:#dc2626;font-weight:600}
.pagination{display:flex;align-items:center;justify-content:center;gap:12px;padding:14px;border-top:1px solid #f0f4fa}
.pg-btn{padding:6px 14px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:13px;cursor:pointer}
.pg-btn:disabled{opacity:.4;cursor:not-allowed}
.pg-info{font-size:13px;color:#7a90b0}
/* Modals */
.modal-overlay{position:fixed;inset:0;z-index:1050;display:flex;align-items:center;justify-content:center;background:rgba(15,25,50,.45);backdrop-filter:blur(3px);opacity:0;pointer-events:none;transition:opacity .2s;padding:16px}
.modal-overlay.open{opacity:1;pointer-events:all}
.modal-box{background:#fff;border-radius:14px;width:100%;max-width:500px;box-shadow:0 20px 60px rgba(0,0,0,.14);animation:slideUp .2s ease;max-height:90vh;overflow-y:auto}
.modal-box--sm{max-width:440px}
@keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #e4eaf2;gap:12px}
.modal-title{font-size:16px;font-weight:700;color:#1a2b45}
.modal-close{width:28px;height:28px;border-radius:7px;border:1px solid #dde5f0;background:transparent;cursor:pointer;font-size:14px;color:#7a90b0;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.modal-body{padding:20px 22px}
.modal-footer{display:flex;justify-content:flex-end;gap:10px;padding:16px 22px;border-top:1px solid #f0f4fa}
.modal-header-icon{width:36px;height:36px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0}
.modal-header-icon--green{background:#dcfce7;color:#166534}
.modal-header-icon--amber{background:#fef3c7;color:#92400e}
.modal-header-icon--grey{background:#f3f4f6;color:#374151}
.modal-sub{font-size:12px;color:#7a90b0;margin:2px 0 0}
.form-row{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:4px}
.form-group{display:flex;flex-direction:column;gap:5px;margin-bottom:14px}
.form-group--full{grid-column:1/-1}
.form-label{font-size:12.5px;font-weight:600;color:#1a2b45}
.form-input{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;outline:none;width:100%;font-family:inherit;box-sizing:border-box}
.form-input:focus{border-color:#004AAD;box-shadow:0 0 0 3px rgba(0,74,173,.07)}
.toggle-label{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:600;color:#1a2b45;cursor:pointer}
.req{color:#e03535}
.opt{font-weight:400;color:#a0aec0;font-size:11px}
.me-1{margin-right:4px}
.field-error{font-size:12px;color:#dc2626;margin:4px 0 0}
.adj-hint{background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:10px 13px;font-size:12.5px;color:#1e40af;margin-bottom:16px}
/* Drawer */
.drawer-backdrop{position:fixed;inset:0;z-index:1040;background:rgba(15,25,50,.35);opacity:0;pointer-events:none;transition:opacity .25s}
.drawer-backdrop.open{opacity:1;pointer-events:all}
.drawer{position:fixed;top:0;right:0;bottom:0;z-index:1041;width:540px;max-width:95vw;background:#fff;box-shadow:-8px 0 40px rgba(0,0,0,.12);transform:translateX(100%);transition:transform .25s ease;display:flex;flex-direction:column}
.drawer.open{transform:translateX(0)}
.drawer-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #e4eaf2;flex-shrink:0}
.drawer-header-left{display:flex;align-items:center;gap:12px}
.drawer-icon{width:36px;height:36px;border-radius:9px;background:#e0f2fe;color:#0891b2;display:flex;align-items:center;justify-content:center;font-size:15px;flex-shrink:0}
.drawer-title{font-size:15px;font-weight:700;color:#1a2b45;margin:0 0 2px}
.drawer-sub{font-size:12px;color:#7a90b0;margin:0}
.drawer-body{flex:1;overflow-y:auto;padding:20px 22px}
/* balance summary */
.balance-summary{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:20px}
.balance-summary-card{border-radius:10px;padding:12px 14px;text-align:center}
.balance-summary-card--blue{background:#eff6ff;border:1px solid #bfdbfe}
.balance-summary-card--green{background:#f0fdf4;border:1px solid #bbf7d0}
.balance-summary-card--red{background:#fef2f2;border:1px solid #fecaca}
.bs-val{font-size:20px;font-weight:800;color:#1a2b45;margin:0 0 2px}
.bs-lbl{font-size:11px;color:#7a90b0;font-weight:600;text-transform:uppercase;letter-spacing:.04em;margin:0}
/* ledger table */
.resp-table{width:100%;border-collapse:collapse;font-size:13px}
.resp-table th{background:#f4f7fb;font-weight:600;color:#5a6a85;padding:9px 12px;text-align:left;font-size:12px;white-space:nowrap}
.resp-table td{padding:10px 12px;border-top:1px solid #f0f4fa;color:#1a2b45;vertical-align:middle}
.resp-table tbody tr:hover{background:#f9fbff}
.resp-date{color:#7a90b0;white-space:nowrap;font-size:12px}
.note-col{color:#5a6a85;max-width:140px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fw-600{font-weight:600}
.type-chip{display:inline-block;font-size:11px;font-weight:700;border-radius:20px;padding:2px 9px;text-transform:capitalize}
.type--earn{background:#dcfce7;color:#166534}
.type--redeem{background:#fef9c3;color:#92400e}
.type--adjust{background:#dbeafe;color:#1d4ed8}
</style>
